import { useEffect, useState } from 'react'
import { X, HeartHandshake, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { getProgramsCatalog, getEnrollments, enrollProgram, type ProgramItem } from '@/api/programs'

export function ProgramsPage({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [catalog, setCatalog] = useState<ProgramItem[]>([])
  const [requested, setRequested] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    getProgramsCatalog().then(setCatalog)
    getEnrollments(userId).then((rows) => setRequested(new Set(rows.map((r) => r.program_id))))
  }, [userId])

  const request = async (p: ProgramItem) => {
    setBusy(p.id)
    const ok = await enrollProgram(userId, p.id)
    if (ok) setRequested((s) => new Set(s).add(p.id))
    setBusy(null)
  }

  const categories = [...new Set(catalog.map((p) => p.category))]

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-background">
      <header className="flex items-center gap-2 h-16 px-4 border-b border-border shrink-0 bg-card">
        <HeartHandshake className="size-5 text-success-text" />
        <div className="flex-1 leading-tight">
          <h1 className="text-base font-semibold text-foreground">Health programs</h1>
          <p className="text-[11px] text-muted-foreground">Specialized care, from vetted partners</p>
        </div>
        <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted" aria-label="Close"><X className="size-5 text-foreground" /></button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg px-4 py-5 space-y-6">
          {categories.map((cat) => (
            <section key={cat}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{cat}</h2>
              <div className="space-y-3">
                {catalog.filter((p) => p.category === cat).map((p) => {
                  const isRequested = requested.has(p.id)
                  return (
                    <div key={p.id} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-foreground">{p.name}</div>
                          <div className="text-[11px] font-medium text-info-text">{p.partner}</div>
                        </div>
                      </div>
                      <p className="mt-1.5 text-sm text-muted-foreground">{p.description}</p>
                      <div className="mt-3">
                        {isRequested ? (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success-text">
                            <CheckCircle2 className="size-4" /> Requested — we'll follow up
                          </span>
                        ) : (
                          <Button size="sm" variant="basic" disabled={busy === p.id} onClick={() => request(p)}>
                            {busy === p.id ? 'Requesting…' : 'Request to join'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          ))}
          <p className="text-center text-[11px] text-muted-foreground">Requests are reviewed by our care team — you'll hear back with next steps.</p>
        </div>
      </div>
    </div>
  )
}
